/**
 * CL-380A — PM project lifecycle (active / archived / historical).
 *
 * Architecture (Phase 1):
 * - Reuses `pmProjects` — no new collection.
 * - Canonical field: `projectLifecycle` = "active" | "archived" | "historical".
 * - Legacy archived flags (`isArchived`, `archivedAt`, status closed/archived) map to "archived"
 *   until an admin sets an explicit lifecycle.
 * - Historical = legacy/reference repository (no workflow signals, no stale warnings).
 * - CL-380C: optional `historicalArchivePhotos[]` on pmProjects for private client gallery (not public portfolio).
 * - Archived = completed in-platform project (past on client portal, no stale on client).
 * - Active = full PM workflows unchanged.
 *
 * Portfolio (`publicProjects`) remains separate — marketing only.
 */

function safeStr(value) {
  return String(value ?? "").trim();
}

function normalizeKey(value) {
  return safeStr(value).toLowerCase().replace(/[\s-]+/g, "_");
}

export const PM_PROJECT_LIFECYCLE = {
  ACTIVE: "active",
  ARCHIVED: "archived",
  HISTORICAL: "historical",
};

const LIFECYCLE_VALUES = new Set(Object.values(PM_PROJECT_LIFECYCLE));

export function normalizeProjectLifecycle(value) {
  const key = normalizeKey(value);
  return LIFECYCLE_VALUES.has(key) ? key : null;
}

/** Legacy archive detection (pre–CL-380A). */
export function isLegacyArchivedProject(project) {
  if (!project) return false;
  if (project.isArchived === true) return true;
  if (project.archived === true) return true;
  if (project.archivedAt) return true;
  const s = safeStr(project.status).toLowerCase();
  return s === "archived" || s === "closed";
}

export function resolveProjectLifecycle(project) {
  const explicit = normalizeProjectLifecycle(project?.projectLifecycle);
  if (explicit) return explicit;
  if (isLegacyArchivedProject(project)) return PM_PROJECT_LIFECYCLE.ARCHIVED;
  return PM_PROJECT_LIFECYCLE.ACTIVE;
}

export function isActiveProjectLifecycle(project) {
  return resolveProjectLifecycle(project) === PM_PROJECT_LIFECYCLE.ACTIVE;
}

export function isArchivedProjectLifecycle(project) {
  return resolveProjectLifecycle(project) === PM_PROJECT_LIFECYCLE.ARCHIVED;
}

export function isHistoricalProjectLifecycle(project) {
  return resolveProjectLifecycle(project) === PM_PROJECT_LIFECYCLE.HISTORICAL;
}

/** Client “Past projects” bucket — archived + historical. */
export function isPastProjectForClient(project) {
  const lifecycle = resolveProjectLifecycle(project);
  return lifecycle === PM_PROJECT_LIFECYCLE.ARCHIVED || lifecycle === PM_PROJECT_LIFECYCLE.HISTORICAL;
}

export function shouldShowAdminWorkflowSignals(project) {
  return isActiveProjectLifecycle(project);
}

export function shouldShowClientWorkflowSignals(project) {
  return isActiveProjectLifecycle(project);
}

/** Admin dashboard “active projects” — excludes archived, historical, and deleted. */
export function isDashboardActiveProject(project) {
  if (!project || project.isDeleted) return false;
  return isActiveProjectLifecycle(project);
}

/** CL-380C — private historical archive photos on `pmProjects.historicalArchivePhotos`. */
export function getHistoricalArchivePhotos(project) {
  const raw = project?.historicalArchivePhotos;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry, index) => {
      const id = safeStr(entry?.id) || `photo-${index}`;
      const url = safeStr(entry?.url);
      if (!url) return null;
      return {
        id,
        url,
        storagePath: safeStr(entry?.storagePath) || null,
        name: safeStr(entry?.name) || "Photo",
        caption: safeStr(entry?.caption) || "",
      };
    })
    .filter(Boolean);
}

/** Client-facing archive notes — one field; referenceNote kept in sync on save. */
export function getHistoricalArchiveNotesText(project) {
  const summary = safeStr(project?.summary);
  const referenceNote = safeStr(project?.referenceNote);
  if (summary) return summary;
  if (referenceNote) return referenceNote;
  return null;
}

export function getHistoricalArchiveFileOrder(project) {
  const raw = project?.historicalArchiveFileOrder;
  if (!Array.isArray(raw)) return [];
  return raw.map((id) => safeStr(id)).filter(Boolean);
}

/** CL-380E — controlled archive document categories (stable keys, UI labels). */
export const HISTORICAL_ARCHIVE_CATEGORY_OPTIONS = [
  { value: "drawing_package", label: "Drawing package" },
  { value: "development_permit", label: "Development permit" },
  { value: "building_permit", label: "Building permit" },
  { value: "survey_rpr", label: "Survey / RPR" },
  { value: "design_model", label: "Design model / rendering" },
  { value: "permit_correspondence", label: "Permit correspondence" },
  { value: "contract_agreement", label: "Contract / agreement" },
  { value: "invoice_receipt", label: "Invoice / receipt" },
  { value: "photo_record", label: "Photo record" },
  { value: "correspondence", label: "Correspondence" },
  { value: "other", label: "Other" },
];

const HISTORICAL_ARCHIVE_CATEGORY_BY_KEY = new Map(
  HISTORICAL_ARCHIVE_CATEGORY_OPTIONS.map((opt) => [opt.value, opt])
);

const HISTORICAL_ARCHIVE_LEGACY_SELECT_PREFIX = "__legacy__:";

export function normalizeHistoricalArchiveCategoryKey(value) {
  const raw = safeStr(value);
  if (!raw) return "";
  const direct = normalizeKey(raw);
  if (HISTORICAL_ARCHIVE_CATEGORY_BY_KEY.has(direct)) return direct;
  for (const opt of HISTORICAL_ARCHIVE_CATEGORY_OPTIONS) {
    if (normalizeKey(opt.label) === direct) return opt.value;
  }
  return direct;
}

export function getHistoricalArchiveCategoryLabel(value) {
  const raw = safeStr(value);
  if (!raw) return "";
  const key = normalizeHistoricalArchiveCategoryKey(raw);
  const opt = HISTORICAL_ARCHIVE_CATEGORY_BY_KEY.get(key);
  if (opt) return opt.label;
  return `Other / legacy: ${raw}`;
}

export function resolveHistoricalArchiveCategorySelectValue(value) {
  const raw = safeStr(value);
  if (!raw) return "";
  const key = normalizeHistoricalArchiveCategoryKey(raw);
  if (HISTORICAL_ARCHIVE_CATEGORY_BY_KEY.has(key)) return key;
  return `${HISTORICAL_ARCHIVE_LEGACY_SELECT_PREFIX}${raw}`;
}

export function historicalArchiveCategoryFromSelectValue(selectValue) {
  const v = safeStr(selectValue);
  if (!v) return null;
  if (v.startsWith(HISTORICAL_ARCHIVE_LEGACY_SELECT_PREFIX)) {
    return v.slice(HISTORICAL_ARCHIVE_LEGACY_SELECT_PREFIX.length) || null;
  }
  if (HISTORICAL_ARCHIVE_CATEGORY_BY_KEY.has(v)) return v;
  return v;
}

/** True when a pmProjectFile belongs to the historical archive document list. */
export function isHistoricalArchiveDocument(file, project) {
  if (!file) return false;
  if (file.isHistoricalArchiveDocument === true) return true;
  if (safeStr(file.historicalArchiveTitle)) return true;
  const sortIdx = Number(file?.historicalArchiveSortIndex);
  if (Number.isFinite(sortIdx)) return true;
  const fid = safeStr(file?.id);
  if (fid && getHistoricalArchiveFileOrder(project).includes(fid)) return true;
  return false;
}

/**
 * Client historical archive — non-deleted archive documents only.
 * When no file is archive-marked, returns all non-deleted files (legacy projects).
 */
export function filterHistoricalArchiveClientFiles(files, project) {
  const list = (Array.isArray(files) ? files : []).filter(
    (f) => safeStr(f?.status).toLowerCase() !== "deleted"
  );
  const hasArchiveMarkers = list.some((f) => isHistoricalArchiveDocument(f, project));
  if (!hasArchiveMarkers) return list;
  return list.filter((f) => isHistoricalArchiveDocument(f, project));
}

/** Admin hint — project files not shown on the client archive page. */
export function getHistoricalArchiveClientHiddenFiles(files, project) {
  const list = (Array.isArray(files) ? files : []).filter(
    (f) => safeStr(f?.status).toLowerCase() !== "deleted"
  );
  const visibleIds = new Set(
    filterHistoricalArchiveClientFiles(list, project).map((f) => safeStr(f?.id)).filter(Boolean)
  );
  return list.filter((f) => {
    const fid = safeStr(f?.id);
    return fid && !visibleIds.has(fid);
  });
}

export function reorderIdList(list, itemId, direction) {
  const ids = Array.isArray(list) ? [...list] : [];
  const index = ids.findIndex((id) => safeStr(id) === safeStr(itemId));
  if (index < 0) return ids;
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= ids.length) return ids;
  const next = [...ids];
  const tmp = next[index];
  next[index] = next[target];
  next[target] = tmp;
  return next;
}

/** CL-380F — HTML5 drag/drop reorder: move dragId row onto dropId row. */
export function reorderListByDragIds(list, dragId, dropId, getId = (item) => safeStr(item?.id)) {
  const drag = safeStr(dragId);
  const drop = safeStr(dropId);
  const items = Array.isArray(list) ? [...list] : [];
  if (!drag || !drop || drag === drop) return items;
  const from = items.findIndex((item) => getId(item) === drag);
  const to = items.findIndex((item) => getId(item) === drop);
  if (from < 0 || to < 0) return items;
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function toTimeMs(value) {
  if (!value) return 0;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  }
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? 0 : value.getTime();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

/** Sort pmProjectFiles for historical archive display (project order, then date). */
export function sortHistoricalArchiveFiles(files, project) {
  const list = (Array.isArray(files) ? files : []).filter(
    (f) => safeStr(f?.status).toLowerCase() !== "deleted"
  );
  const order = getHistoricalArchiveFileOrder(project);
  const byId = new Map();
  const withoutId = [];
  list.forEach((f) => {
    const fid = safeStr(f?.id);
    if (fid) byId.set(fid, f);
    else withoutId.push(f);
  });

  if (order.length) {
    const ordered = [];
    order.forEach((id) => {
      const fid = safeStr(id);
      if (fid && byId.has(fid)) {
        ordered.push(byId.get(fid));
        byId.delete(fid);
      }
    });
    const remainder = [...byId.values()].sort((a, b) => {
      const bms = Math.max(toTimeMs(b?.updatedAt), toTimeMs(b?.createdAt));
      const ams = Math.max(toTimeMs(a?.updatedAt), toTimeMs(a?.createdAt));
      return bms - ams;
    });
    return [...ordered, ...remainder, ...withoutId];
  }

  return [...list].sort((a, b) => {
    const ai = Number(a?.historicalArchiveSortIndex);
    const bi = Number(b?.historicalArchiveSortIndex);
    if (Number.isFinite(ai) && Number.isFinite(bi) && ai !== bi) return ai - bi;
    const bms = Math.max(toTimeMs(b?.updatedAt), toTimeMs(b?.createdAt));
    const ams = Math.max(toTimeMs(a?.updatedAt), toTimeMs(a?.createdAt));
    if (bms !== ams) return bms - ams;
    return safeStr(a?.name).localeCompare(safeStr(b?.name), undefined, { numeric: true });
  });
}

export function getHistoricalArchiveFileDisplay(file) {
  const title =
    safeStr(file?.historicalArchiveTitle) || safeStr(file?.name) || "Document";
  const categoryRaw =
    safeStr(file?.historicalArchiveCategory) || safeStr(file?.category) || "";
  return {
    title,
    description:
      safeStr(file?.historicalArchiveDescription) || safeStr(file?.description) || "",
    documentDate: safeStr(file?.historicalArchiveDocumentDate) || "",
    category: categoryRaw ? getHistoricalArchiveCategoryLabel(categoryRaw) : "",
    categoryKey: categoryRaw ? normalizeHistoricalArchiveCategoryKey(categoryRaw) : "",
  };
}

export function reorderHistoricalArchivePhotos(photos, photoId, direction) {
  const list = Array.isArray(photos) ? [...photos] : [];
  const index = list.findIndex((p) => safeStr(p?.id) === safeStr(photoId));
  if (index < 0) return list;
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const tmp = next[index];
  next[index] = next[target];
  next[target] = tmp;
  return next;
}

export function getProjectLifecycleLabel(project) {
  const lifecycle = resolveProjectLifecycle(project);
  if (lifecycle === PM_PROJECT_LIFECYCLE.HISTORICAL) return "Historical";
  if (lifecycle === PM_PROJECT_LIFECYCLE.ARCHIVED) return "Archived";
  return "Active";
}

export function getClientPastProjectLabel(project) {
  if (isHistoricalProjectLifecycle(project)) return "Historical project";
  if (isArchivedProjectLifecycle(project)) return "Past project";
  return null;
}

/** Client/admin archive detail — metadata only; no invented years or scope. */
export function buildProjectArchiveDisplayModel(project) {
  if (!project) return null;

  const lifecycle = resolveProjectLifecycle(project);
  const lifecycleLabel = getClientPastProjectLabel(project) || "Past project";
  const clientName = safeStr(project.clientName) || null;
  const address = safeStr(project.address) || null;
  const city = safeStr(project.city) || null;
  const province = safeStr(project.province) || null;
  const locationTail = [city, province].filter(Boolean).join(", ");
  const locationLine =
    [address, locationTail].filter(Boolean).join(" · ") || null;
  const completedYear = safeStr(project.historicalCompletedYear) || null;
  const notesText = getHistoricalArchiveNotesText(project);
  const scopeText = safeStr(project.scope) || safeStr(project.services) || null;

  return {
    lifecycle,
    lifecycleLabel,
    title: safeStr(project.name) || "Project",
    clientName,
    locationLine,
    completedYear,
    notesText,
    scopeText,
    purposeBullets: [
      "View historical drawings and documents",
      "Keep permit/design records in one place",
      "Revisit project history if future work comes up",
    ],
  };
}

export const ADMIN_HISTORICAL_ARCHIVE_BANNER_COPY =
  "Historical project archive. Use this workspace to store legacy drawings, documents, and notes. Active workflow signals are intentionally hidden.";

export function buildHistoricalProjectCreatePayload(form, options = {}) {
  const uid = options.createdByUid || null;
  const name = safeStr(form?.name);
  const completedYear = safeStr(form?.historicalCompletedYear || form?.completedYear);
  const summaryParts = [safeStr(form?.summary)];
  if (completedYear) summaryParts.push(`Completed ${completedYear}.`);
  const summary = summaryParts.filter(Boolean).join(" ").trim() || null;

  return {
    name,
    clientName: safeStr(form?.clientName) || null,
    address: safeStr(form?.address) || null,
    city: safeStr(form?.city) || "Calgary",
    province: safeStr(form?.province) || "AB",
    country: safeStr(form?.country) || "Canada",
    stage: "complete",
    summary,
    projectLifecycle: PM_PROJECT_LIFECYCLE.HISTORICAL,
    historicalCompletedYear: completedYear || null,
    referenceNote: safeStr(form?.referenceNote) || summary,
    createdAt: options.createdAt || null,
    createdBy: uid,
    updatedAt: options.updatedAt || null,
    updatedBy: uid,
    lastActivityAt: options.lastActivityAt || null,
    lastActivityType: "historical_reference",
  };
}
